import { AbacoWebModule, RouteConfig, RouterReadyEvent, VueRouter } from "@abaco/web-common/src/app";
import { HTTPClient } from '@abaco/web-common/src/HTTP';
import SsoVuexModule from "./state";
import { createRouter } from "./routes";
import TokensResources, { LoginCallback as _LoginCallback, BasicLoginCallback } from './TokensResources';
import PasswordResources from './PasswordResources';
import SpidResources from './SpidResources';
import 'animate.css';
import globals from "./globals";
import RegisterResource from "./RegisterResource";
import CompetencesResources from "./CompetencesResources";
import { ApplicationReadyEvent } from "@abaco/web-common/src/app/Application";
import { BASE_WITH_TENANT } from "./models/Constants";

export const MODULE_ID = "sso_web_module";

export type LoginCallback = _LoginCallback;
export interface SsoOptions {
    environment?: string;
    skipPrivacyChecks?: boolean;
    loginContext?: string;
    useSSO1?: boolean;
    loginPage?: string;
    basicAuth?: BasicLoginCallback;
    hidePasswordRecovery?: boolean;
    tenant?: string;
    useApiGw?: boolean;
    hideLoginBaseTab?: boolean;
}

export default class SSOModule implements AbacoWebModule {
    public static MODULE_ID = MODULE_ID;
    public readonly environment: string = "portal"
    public readonly hidePasswordRecovery: boolean = false;
    public readonly loginPage: string = "login-page";
    public readonly tenant: string = "_";
    public readonly hideLoginBaseTab: boolean = false;

    private lastValidatedSso1Token = null as string | null;

    private httpClient!: HTTPClient;
    private tokensRes!: TokensResources;
    private passwordRes!: PasswordResources;
    private competenceRes!: CompetencesResources;
    private spdRes!: SpidResources;
    private regRes!: RegisterResource;
    private router!: VueRouter;

    private loginContext?: string;
    private readonly skipPrivacyChecks!: boolean;
    private readonly useSSO1!: boolean;
    private basicAuth?: BasicLoginCallback;
    private onLogin?: LoginCallback;
    private useApiGw?: boolean;

    readonly id = MODULE_ID;
    readonly additionalI18N = ["web_components"];
    readonly routes!: RouteConfig[];
    readonly vuexModule = new SsoVuexModule();
    readonly globalComponents = globals;
    readonly forceTopLevelRoutes = true;

    constructor(opts: SsoOptions | LoginCallback, onLogin?: LoginCallback) {
        if (opts) {
            if (typeof opts === "object") {
                this.loginContext = opts.loginContext;
                this.environment = opts.environment || "portal";
                this.skipPrivacyChecks = opts.skipPrivacyChecks || false;
                this.useSSO1 = opts.useSSO1 || false;
                this.loginPage = opts.useApiGw && !opts.loginPage ? "login-error" : opts.loginPage || "login-page";
                this.basicAuth = opts.basicAuth || undefined;
                this.hidePasswordRecovery = opts.hidePasswordRecovery || false;
                this.tenant = opts.tenant || "_";
                this.useApiGw = opts.useApiGw || false;
                this.hideLoginBaseTab = opts.hideLoginBaseTab || false;
            } else {
                this.onLogin = opts;
            }
        }

        if (onLogin) {
            this.onLogin = onLogin;
        }
        this.routes = createRouter(this.loginPage, this.useApiGw, this.environment);
    }

    public get http(): HTTPClient {
        return this.httpClient;
    }

    public get tokensResources(): TokensResources {
        return this.tokensRes;
    }

    public get passwordResources(): PasswordResources {
        return this.passwordRes;
    }

    public get spidResources(): SpidResources {
        return this.spdRes;
    }

    public get registerResources(): RegisterResource {
        return this.regRes;
    }

    public get competenceResources(): CompetencesResources {
        return this.competenceRes;
    }

    public get hasRegister(): boolean {
        return this.environment === "r2u";
    }

    public async isFunctionEnabled(ctx: string, fun: string): Promise<boolean> {
        if (this.vuexModule.userFunctions === null) {
            await this.tokensResources.loadUserFunctions();
        }
        return this.vuexModule.userFunctions?.[ctx]?.includes(fun) || false;
        // Devo aver fumato qualcosa di pesante per scrivere la riga qui sopra... quindi riporto come commento cosa fa, per i posteri:
        // if (!this.vuexModule.userFunctions) {
        //     return false;
        // }
        // const ctxFunctions = this.vuexModule.userFunctions[ctx];
        // return ctxFunctions && ctxFunctions.includes(fun);
    }

    public async hasContext(ctx: string): Promise<boolean> {
        if (this.vuexModule.userContexts != null) {
            return this.vuexModule.userContexts.get(ctx) || false;
        }
        const ctxs = await this.tokensResources.loadUserContexts();
        return ctxs.get(ctx) || false;
    }

    public async checkContext(): Promise<boolean> {
        if (!this.vuexModule.user) {
            return false;
        }
        if (!this.loginContext) {
            return true;
        }

        const ctxs = await this.tokensResources.loadUserContexts();
        return ctxs.get(this.loginContext) || false;
    }

    public async getSSOToken(): Promise<string> {
        return this.tokensRes.getSSOToken();
    }

    public getAuthTokenExpiresAt(): number {
        return this.tokensRes.getAuthTokenExpiresAt();
    }

    private get isPortalR2U() {
        return this.environment === "r2u";
    }

    private get isPortalAriaGeophoto() {
        return this.environment === "portal-aria-geophoto";
    }

    public addAnauthorizedHandler(http: HTTPClient) {
        const router = this.router;
        const loginPage = this.loginPage;
        const isPortalAriaGeophoto = this.isPortalAriaGeophoto;
        http.addErrorHandler({
            onResponseError(err, response) {
                if (response && response.status === 401) {
                    if (router.currentRoute.name !== "login" && !isPortalAriaGeophoto) {
                        console.warn("addAnauthorizedHandler - redirecting to login");
                        router.push({ name: "login", replace: true, params: { redirectTo: router.currentRoute.path, initPage: loginPage } });
                    }
                }
            }
        });
    }

    async onRouterReady(event: RouterReadyEvent) {
        this.router = event.router
        this.httpClient = event.http;
        this.competenceRes = new CompetencesResources(event.http);
        this.tokensRes = new TokensResources(event.http, this.vuexModule, this.competenceRes, BASE_WITH_TENANT.replace("{tenant}", this.tenant), this.onLogin, undefined, this.skipPrivacyChecks, this.useSSO1, this.basicAuth, this.environment, this.useApiGw);
        this.passwordRes = new PasswordResources(event.http, this.tokensRes, event.router);
        this.spdRes = new SpidResources(event.http);
        this.regRes = new RegisterResource();

        this.addAnauthorizedHandler(this.httpClient);

        event.router.beforeEach(async (to, from, next) => {
            const redirect = to.matched.some(r => r.name === "login") ? "/" : to.fullPath;
            const ssoToken = to.query.SSO_TOKEN as string | null;
            const spidErrorMsg = to.query.SPID_ERROR_MESSAGE;
            const spidErrorStatus = to.query.SPID_ERROR_STATUS;
            if (ssoToken && !spidErrorStatus && ssoToken !== this.lastValidatedSso1Token) {
                this.tokensRes.logout();
                this.tokensRes.exchangeSso1Token(ssoToken, this.loginContext)
                    .then(async () => {
                        this.lastValidatedSso1Token = ssoToken;
                        const haveContext = await this.checkContext();
                        if (!haveContext) {
                            this.tokensResources.logout();
                            const curErrorMsg = this.isPortalR2U ? "err.mail-not-confirmed" : "err.context-notlinked";
                            console.warn("Context not linked, redirecting to login");
                            next({ name: "login", replace: true, params: { initPage: "login-error", errorMsg: curErrorMsg, errorStatus: "403" } });
                        }
                        else if (!this.hasAcceptedPrivacy()) {
                            console.warn("Privacy not accepted, redirecting to login");
                            next({ name: "login", replace: true, params: { initPage: "privacy-check-page", redirectTo: redirect } })
                        }
                        else if (to.matched.some(r => r.name === "login")) {
                            next({ path: "/" })
                        } else {
                            next();
                        }
                    })
                    .catch((err) => {
                        console.error(err);
                        const status = err.response.status;
                        const msg = err.response.statusText;
                        console.warn("Error during SSO token exchange, redirecting to login");
                        next({ name: "login", replace: true, params: { initPage: "login-error", errorMsg: msg, errorStatus: status } });
                    });
                return;
            }
            if (spidErrorStatus) {
                console.warn("SPID error, redirecting to login");
                next({ name: "login", params: { errorMsg: spidErrorMsg as string, errorStatus: spidErrorStatus as string, initPage: "spid-error" } })
                return;
            }

            const authRequired = to.matched.some((m) => !(m.meta?.guest))
            const expiredAllowed = to.matched.filter((m) => m.path !== "").every((m) => m.meta?.expiredAllowed) && to.path !== "/";

            if (authRequired) {
                if (!this.vuexModule.user && !(await this.tokensRes.resumeSession())) {
                    console.warn("No user, redirecting to login");
                    next({ name: "login", replace: true, params: { redirectTo: redirect, initPage: this.loginPage } });
                    return;
                }
                if (!expiredAllowed && this.vuexModule.basicContractExpired) {
                    // TODO uncomment when contract ready
                    // next({ name: "login", replace: true, params: { redirectTo: redirect, initPage: this.loginPage } });
                    if (from.name != "bc-subscribe") {
                        next({ name: "bc-subscribe", replace: true, params: { redirectTo: redirect } });
                    }
                    return;
                }
            }
            next();
        });
    }

    onApplicationReady(event: ApplicationReadyEvent) {
        this.tokensResources.setGtag(event.gtag)
    }

    hasAcceptedPrivacy() {
        if (
            this.vuexModule.privacyChecks &&
            (!this.vuexModule.privacyChecks["PORTAL|ACCEPT_PRIVACY_1"] ||
                (this.environment === "r2u" &&
                    (!this.vuexModule.privacyChecks["PORTAL|ACCEPT_TOS"] || !this.vuexModule.privacyChecks["PORTAL|ACCEPT_TOS_18"])))
        ) {
            return false
        }
        return true;
    }

}
